import React, { useMemo } from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import { AppFontStyle } from "../../../styles/AppFontStyle";
import { AppColors } from "../../../utility/AppColors";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../../utility/NavigationHelper";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppScreens } from "../../../screens/Utility/AppScreens";
import { GameShow } from "../../../utility/types/gameShowTypes";
import { UserData } from "../../../screens/Auth/WebScreens/OnGoingEvents/types/event.types";
import ImageComponent from "../../ImageComponent";
import Skeletons from "../../skeleton-components/Skeletons";
import { AppImages } from "../../../utility/AppImages";
import { EventDetails } from "../../../screens/Auth/WebScreens/OnGoingEvents/types/event.types";
import { LinearGradient } from "expo-linear-gradient";
import Avatar from "../../choose-avatar-components/Avatar";

type PlayersViewProps = {
  showLoader?: boolean;
  styles?: any;
  details: EventDetails;
  userData: UserData;
  notificationData?: any;
  notfoundMessage?: string;
  isOrganiserId?: boolean;
  showAPILoader?: boolean;
  onSelectOpponent?: () => void;
  onAcceptChallenge?: () => void;
  onRejectChallenge?: () => void;
  IsEventCanceled?: boolean;
  IsEventCompleted?: boolean;
};

const PlayersView: React.FC<PlayersViewProps> = ({
  showLoader,
  styles,
  details,
  notificationData,
  notfoundMessage,
  isOrganiserId,
  showAPILoader,
  onSelectOpponent,
  onAcceptChallenge,
  onRejectChallenge,
  IsEventCanceled,
  IsEventCompleted,
  userData,
}) => {
  const deviceSize = useDeviceSize();
  const navigation = useNavigation();
  console.log("notfoundMessage", notfoundMessage);

  const handleParticipantPress = (userID?: string) => {
    if (userID) {
      navigateToScreen(navigation, AppScreens.UserProfile, {
        username: userID,
      });
    }
  };

  const isSmallDevice =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  // Helper function to determine if check-in window is active
  const isCheckInWindowActive = useMemo(() => {
    if (!details?.eventStartTime) return false;

    const currentTime = new Date().getTime();
    const eventStartTime = new Date(details.eventStartTime).getTime();
    const oneHourBeforeEvent = eventStartTime - 90 * 60 * 1000; // 1 hour before event

    // Check-in window is active if current time is between 1 hour before event and event start time
    return currentTime >= oneHourBeforeEvent && currentTime < eventStartTime;
  }, [details?.eventStartTime]);

  const renderSkeletonLoader = () => (
    <View>
      {[...Array(2)].map((_, index) => (
        <Skeletons
          key={index}
          height={20}
          width={isSmallDevice ? "75px" : "200px"}
          style={{
            borderRadius: 10,
            marginTop: 10,
            marginBottom: index === 1 ? (isSmallDevice ? 30 : 50) : 0,
          }}
        />
      ))}
      <Skeletons
        height={isSmallDevice ? 125 : 220}
        width={isSmallDevice ? "95px" : "195px"}
        style={{ borderRadius: 10, marginTop: 10 }}
      />
    </View>
  );

  const renderPlayerView = (playerIndex: number) => {
    const participant = details?.participantDetails?.[playerIndex];
    const isOrganiser = playerIndex === 0;
    const isChallenger = playerIndex === 1;

    // Determine what label to show
    const playerLabel = isOrganiser ? "Kaller" : "Opponent";

    // Determine status text and color
    const getStatusInfo = () => {
      if (isChallenger) {
        // Check if opponent rejected
        if (notificationData?.actionStatus === "rejected") {
          return {
            text: "Challenge Rejected",
            color: AppColors.red_AF0707,
          };
        }

        // Check if opponent accepted and check-in window is active
        if (
          notificationData?.actionStatus === "accepted" &&
          isCheckInWindowActive
        ) {
          // Check if challenger has checked in
          const hasCheckedIn = details?.checkInStatus?.challenger;
          return {
            text: hasCheckedIn ? "Checked in" : "Check in Pending",
            color: hasCheckedIn ? "#51AF07" : "#FACC15",
          };
        }

        // Check if opponent is pending or no status yet
        if (
          notificationData?.actionStatus === "pending" ||
          !notificationData?.actionStatus
        ) {
          return {
            text: "Challenge Pending",
            color: "#FACC15",
          };
        }

        // If accepted but check-in window not active
        if (notificationData?.actionStatus === "accepted") {
          return {
            text: "Challenge Accepted",
            color: "#51AF07",
          };
        }
      }

      // For organiser, show check-in status if window is active and challenge accepted
      if (
        isOrganiser &&
        isCheckInWindowActive &&
        notificationData?.actionStatus === "accepted"
      ) {
        const hasCheckedIn = details?.checkInStatus?.organiser;
        return {
          text: hasCheckedIn ? "Checked in" : "Check in Pending",
          color: hasCheckedIn ? "#51AF07" : "#FACC15",
        };
      }

      return null;
    };

    const statusInfo = getStatusInfo();

    return (
      <View style={isSmallDevice ? { width: "40%" } : undefined}>
        {/* Label at the top */}
        <Text
          style={[
            AppFontStyle.BOLD_ANYBODY_18,
            {
              marginBottom: 10,
              fontWeight: "700",
              color: "#DC2626",
            },
          ]}
        >
          {playerLabel}
        </Text>

        {/* Card with profile picture, name, and status */}
        {participant ? (
          <TouchableOpacity
            onPress={() => handleParticipantPress(participant?.userID)}
            style={[
              {
                width: isSmallDevice ? "100%" : undefined,
                maxWidth: isSmallDevice ? "100%" : undefined,
              },
            ]}
          >
            <LinearGradient
              colors={
                isSmallDevice
                  ? ["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]
                  : ["transparent", "transparent"]
              }
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={[
                styles.playerView,
                {
                  alignItems: "center",
                  padding: isSmallDevice ? 9 : 10,
                  width: isSmallDevice ? "100%" : undefined,
                  maxWidth: isSmallDevice ? "100%" : undefined,
                  height: isSmallDevice ? 153 : undefined,
                  minHeight: isSmallDevice ? undefined : 280,
                  justifyContent: "center",
                  borderRadius: 8,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                  gap: isSmallDevice ? 12 : undefined,
                },
              ]}
            >
              {isSmallDevice ? (
                <View
                  style={{
                    width: 72.685,
                    height: 72.685,
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    backgroundColor: AppColors.lightPurple_604e74,
                    borderRadius: 36.34,
                    overflow: "hidden",
                  }}
                >
                  <Avatar
                    src={participant?.profilePicture}
                    variant="profile"
                  />
                </View>
              ) : (
                <ImageComponent
                  url={participant?.profilePicture}
                  style={[
                    styles.imgstyle,
                    {
                      resizeMode: "cover",
                      borderRadius: 80,
                    },
                  ]}
                />
              )}
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: isSmallDevice ? 0 : 10,
                }}
              >
                {statusInfo && (
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: statusInfo.color,
                      marginRight: 5,
                    }}
                  />
                )}
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_14,
                    {
                      fontWeight: "700",
                      color: AppColors.white,
                      fontSize: isSmallDevice ? 14.415 : 14,
                      lineHeight: isSmallDevice ? 16.65 : undefined, // 115.5% of 14.415
                    },
                  ]}
                >
                  {participant?.userID}
                </Text>
              </View>
              <View
                style={{
                  minHeight: isSmallDevice ? undefined : 32,
                  marginTop: isSmallDevice ? 0 : 8,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {statusInfo && (
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_12,
                      {
                        color: statusInfo.color,
                        fontWeight: isSmallDevice ? "600" : "700",
                        fontSize: isSmallDevice ? 10 : 12,
                        lineHeight: isSmallDevice ? 11.55 : undefined, // 115.5% of 10
                        textAlign: "center",
                      },
                    ]}
                    numberOfLines={2}
                    ellipsizeMode="tail"
                  >
                    {statusInfo.text}
                  </Text>
                )}
              </View>
            </LinearGradient>
          </TouchableOpacity>
        ) : isChallenger ? (
          <LinearGradient
            colors={
              isSmallDevice
                ? ["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]
                : ["transparent", "transparent"]
            }
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              styles.playerView,
              {
                alignItems: "center",
                padding: isSmallDevice ? 9 : 10,
                width: isSmallDevice ? "100%" : undefined,
                maxWidth: isSmallDevice ? "100%" : undefined,
                height: isSmallDevice ? 153 : undefined,
                minHeight: isSmallDevice ? undefined : 280,
                justifyContent: "center",
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                gap: isSmallDevice ? 12 : undefined,
              },
            ]}
          >
            {isSmallDevice ? (
              <View
                style={{
                  width: 72.685,
                  height: 72.685,
                  justifyContent: "center",
                  alignItems: "center",
                  flexShrink: 0,
                  backgroundColor: AppColors.lightPurple_604e74,
                  borderRadius: 36.34,
                  overflow: "hidden",
                }}
              >
                <Image
                  source={AppImages.icKoDefaultOpponent}
                  style={{
                    width: 72.685,
                    height: 72.685,
                    resizeMode: "contain",
                  }}
                />
              </View>
            ) : (
              <Image
                source={AppImages.icKoDefaultOpponent}
                style={[
                  styles.imgstyle,
                  {
                    resizeMode: "contain",
                  }
                ]}
              />
            )}
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginTop: isSmallDevice ? 0 : 10,
              }}
            >
              <View
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: AppColors.gray_BBB,
                  marginRight: 5,
                }}
              />
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_14,
                  {
                    fontWeight: "700",
                    color: AppColors.gray_BBB,
                    fontSize: isSmallDevice ? 14.415 : 14,
                    lineHeight: isSmallDevice ? 16.65 : undefined,
                  },
                ]}
              >
                -------
              </Text>
            </View>
            <View
              style={{
                minHeight: isSmallDevice ? undefined : 32,
                marginTop: isSmallDevice ? 0 : 8,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {statusInfo && (
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_12,
                    {
                      color: statusInfo.color,
                      fontWeight: isSmallDevice ? "600" : "700",
                      fontSize: isSmallDevice ? 10 : 12,
                      lineHeight: isSmallDevice ? 11.55 : undefined,
                      textAlign: "center",
                    },
                  ]}
                  numberOfLines={2}
                  ellipsizeMode="tail"
                >
                  {statusInfo.text}
                </Text>
              )}
            </View>
          </LinearGradient>
        ) : null
        }

        {/* Select Opponent Button - Show inside card for Desktop */}
        {
          !isSmallDevice &&
          isChallenger &&
          isOrganiserId &&
          (!participant || notificationData?.actionStatus === "rejected") && (
            <TouchableOpacity
              onPress={() => {
                if (onSelectOpponent && !showAPILoader) {
                  onSelectOpponent();
                }
              }}
              disabled={
                showAPILoader ||
                notificationData?.actionStatus === "accepted" ||
                IsEventCanceled ||
                IsEventCompleted
              }
              style={{
                marginTop: 15,
                paddingVertical: 10,
                paddingHorizontal: 30,
                backgroundColor: "transparent",
                borderWidth: 1,
                borderColor: AppColors.white,
                borderRadius: 8,
                alignSelf: "center",
                opacity:
                  showAPILoader ||
                    notificationData?.actionStatus === "accepted" ||
                    IsEventCanceled ||
                    IsEventCompleted
                    ? 0.5
                    : 1,
              }}
            >
              <Text
                style={[
                  AppFontStyle.BOLD_ANYBODY_12,
                  { color: AppColors.white, fontWeight: "700" },
                ]}
              >
                {showAPILoader ? "Processing..." : "Select opponent"}
              </Text>
            </TouchableOpacity>
          )
        }

        {/* Accept/Reject Buttons - Show for challenger when challenge is pending and user is the challenger */}
        {
          isChallenger &&
          !isOrganiserId &&
          participant &&
          notificationData?.actionStatus === "pending" &&
          participant?.userID === userData?.username && (
            <View
              style={{
                flexDirection: "row",
                gap: 10,
                marginTop: 15,
                justifyContent: "center",
              }}
            >
              {/* Reject Button */}
              <TouchableOpacity
                onPress={() => {
                  if (onRejectChallenge && !showAPILoader) {
                    onRejectChallenge();
                  }
                }}
                disabled={showAPILoader || IsEventCanceled || IsEventCompleted}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 25,
                  backgroundColor: "transparent",
                  borderWidth: 2,
                  borderColor: AppColors.red_ED1F24,
                  borderRadius: 8,
                  opacity:
                    showAPILoader || IsEventCanceled || IsEventCompleted
                      ? 0.5
                      : 1,
                }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_12,
                    { color: AppColors.buttonRed_DC2626, fontWeight: "700" },
                  ]}
                >
                  {showAPILoader ? "Processing..." : "Reject"}
                </Text>
              </TouchableOpacity>

              {/* Accept Button */}
              <TouchableOpacity
                onPress={() => {
                  if (onAcceptChallenge && !showAPILoader) {
                    onAcceptChallenge();
                  }
                }}
                disabled={showAPILoader || IsEventCanceled || IsEventCompleted}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 25,
                  backgroundColor: AppColors.appGreenColor_51AF07,
                  borderRadius: 8,
                  opacity:
                    showAPILoader || IsEventCanceled || IsEventCompleted
                      ? 0.5
                      : 1,
                }}
              >
                <Text
                  style={[
                    AppFontStyle.BOLD_ANYBODY_12,
                    { color: AppColors.white, fontWeight: "700" },
                  ]}
                >
                  {showAPILoader ? "Processing..." : "Accept"}
                </Text>
              </TouchableOpacity>
            </View>
          )
        }
      </View >
    );
  };

  return (
    <View>
      <View style={[
        styles.playersView,
        isSmallDevice && { flexDirection: "row", alignItems: "center" }
      ]}>
        {showLoader ? renderSkeletonLoader() : renderPlayerView(0)}
        {showLoader ? (
          <View style={{ justifyContent: "center" }}>
            <Skeletons
              height={isSmallDevice ? 50 : 70}
              width={isSmallDevice ? "50px" : "70px"}
              style={{ borderRadius: 10, marginTop: 10 }}
              circle
            />
          </View>
        ) : (
          <View style={isSmallDevice ? { width: "20%", alignItems: "center", justifyContent: "center" } : undefined}>
            <Image
              source={AppImages.icVsLogo}
              style={[
                styles.VsImg,
                isSmallDevice && { width: 63, height: 142.06 }
              ]}
            />
          </View>
        )}
        {showLoader ? renderSkeletonLoader() : renderPlayerView(1)}
      </View>
      {/* Select Opponent Button - Moved outside player view for better mobile UI - MOBILE ONLY */}
      {
        isSmallDevice &&
        isOrganiserId &&
        (!details?.participantDetails?.[1] || notificationData?.actionStatus === "rejected") && (
          <TouchableOpacity
            onPress={() => {
              if (onSelectOpponent && !showAPILoader) {
                onSelectOpponent();
              }
            }}
            disabled={
              showAPILoader ||
              notificationData?.actionStatus === "accepted" ||
              IsEventCanceled ||
              IsEventCompleted
            }
            style={{
              marginTop: 15,
              paddingVertical: 10,
              paddingHorizontal: 30,
              backgroundColor: "transparent",
              borderWidth: 1,
              borderColor: AppColors.white,
              borderRadius: 8,
              alignSelf: "flex-end",
              marginRight: 10,
              opacity:
                showAPILoader ||
                  notificationData?.actionStatus === "accepted" ||
                  IsEventCanceled ||
                  IsEventCompleted
                  ? 0.5
                  : 1,
            }}
          >
            <Text
              style={[
                AppFontStyle.BOLD_ANYBODY_12,
                { color: AppColors.white, fontWeight: "700" },
              ]}
            >
              {showAPILoader ? "Processing..." : "Select opponent"}
            </Text>
          </TouchableOpacity>
        )
      }
    </View>
  );
};

export default PlayersView;
